import { useCallback, useRef, useState } from "react";
import AnchoredPanel from "../../AnchoredPanel/AnchoredPanel";
import "./SearchSelect.css";

/**
 * Dropdown with a search box and a checkbox list — the same look as the
 * company-admin filter dropdowns. Multi-select by default; pass
 * `multiple={false}` for a single choice that closes on pick.
 *
 * @param {object} props
 * @param {{ value: string, label: string }[]} props.options
 * @param {string[]|string} [props.value] string[] when multiple, else string
 * @param {(value: string[]|string) => void} props.onChange
 * @param {boolean} [props.multiple=true]
 * @param {string} [props.allLabel] multiple only: the "select all" row, and the label when nothing/everything is picked
 * @param {string} [props.placeholder] shown when nothing is picked
 * @param {string} [props.noun="selected"] "3 {noun}"
 * @param {boolean} [props.disabled]
 * @param {string} [props.title] tooltip on the trigger
 * @param {string} [props.ariaLabel]
 * @param {(value: string) => void} [props.onCreate] offers "Create <typed>" when the search matches nothing exactly
 * @param {string} [props.createLabel] verb for that row, e.g. "Add"
 * @param {"cell"} [props.variant] cell: sits inside a grid cell, quiet until hovered
 */
export default function SearchSelect({
  options,
  value,
  onChange,
  multiple = true,
  allLabel,
  placeholder = "Select…",
  noun = "selected",
  disabled = false,
  title,
  ariaLabel,
  onCreate,
  createLabel = "Create",
  variant,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const picked = multiple ? value || [] : value ? [value] : [];
  const allPicked = options.length > 0 && picked.length === options.length;
  const needle = query.trim().toLowerCase();
  const visible = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;

  const label =
    picked.length === 0 || (multiple && allPicked && allLabel)
      ? allLabel || placeholder
      : picked.length === 1
        ? options.find((o) => o.value === picked[0])?.label || `1 ${noun}`
        : `${picked.length} ${noun}`;

  const typed = query.trim();
  const canCreate = !!onCreate && !!typed && !options.some((o) => o.label.toLowerCase() === typed.toLowerCase());

  const create = () => {
    onCreate(typed);
    close();
  };

  const pick = (optionValue) => {
    if (!multiple) {
      onChange(optionValue);
      close();
      return;
    }
    onChange(picked.includes(optionValue) ? picked.filter((v) => v !== optionValue) : [...picked, optionValue]);
  };

  return (
    <div className={`gss${variant ? ` gss--${variant}` : ""}`} ref={rootRef}>
      <button
        type="button"
        className={`gss-trigger${multiple && picked.length && !allPicked ? " gss-trigger--active" : ""}`}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className="gss-label">{label}</span>
        <span className="gss-caret" aria-hidden="true">
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open ? (
        <AnchoredPanel anchorRef={rootRef} onClose={close} className="gss-panel" matchWidth>
          <input
            autoFocus
            type="search"
            className="gss-search"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="gss-list">
            {multiple && allLabel && !needle ? (
              <label className="gss-option gss-option--all">
                <input
                  type="checkbox"
                  checked={allPicked}
                  onChange={() => onChange(allPicked ? [] : options.map((o) => o.value))}
                />
                {allLabel}
              </label>
            ) : null}
            {visible.length === 0 && !canCreate ? <p className="gss-empty">No matches</p> : null}
            {canCreate ? (
              <button type="button" className="gss-option gss-create" onClick={create}>
                {createLabel} “{typed}”
              </button>
            ) : null}
            {visible.map((option) => (
              <label
                key={option.value}
                className={`gss-option${picked.includes(option.value) ? " gss-option--picked" : ""}`}
              >
                <input
                  type={multiple ? "checkbox" : "radio"}
                  checked={picked.includes(option.value)}
                  onChange={() => pick(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </AnchoredPanel>
      ) : null}
    </div>
  );
}
